import { combine, createEffect, createEvent, createStore } from 'effector';
import IGame from '@interfaces/IGame';
import Tag from '@interfaces/Tag';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from './pagination';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import { closeModal } from './modals';
import { get } from 'lodash';
import { SorterResult } from 'antd/lib/table/interface';
import { LOCALE } from '@stores/localeWeight';

type UpdateCreateGameParams = {
  id?: IGame['id'];
  gameData: string;
  imagesData: FormData[];
  tags: Tag[] | null;
};

type CheckGameCodeParams = {
  code: string;
};

export const reset = createEvent('Posts store reset');

const defaultFilters = {
  sort: 'id,desc',
};

export const getList = createEffect({
  name: 'getList',
  async handler(params?: {
    pagination?: PaginationProps;
    filters?: Record<string, any>;
    sorter?: SorterResult<IGame>;
  }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    let filtersParams = filters.getState();

    const sorterParams: { sort?: string } = {};
    if (params?.sorter) {
      sorterParams.sort = params.sorter.order
        ? `${params.sorter.columnKey},${params.sorter.order.replace('end', '')}`
        : defaultFilters.sort;
    }

    if (params?.sorter && !params.filters) {
      setFilters({ ...filtersParams, ...sorterParams });
    }

    if (params?.filters) {
      setFilters({ ...params.filters, ...sorterParams });
    }

    filtersParams = filters.getState();

    const { current } = paginationParams;

    const requestParams = {
      paged: true,
      size: localStorage.getItem('gamesPageSize'),
      page: (current || 1) - 1,
      ...removeUndefinedFilters(filtersParams),
      ...sorterParams,
    };

    const currentLocale = localStorage.getItem('localeWeight') as LOCALE;
    const requestObj = {
      ...api.games.getList,
      headers: { 'Accept-Language': currentLocale },
    };

    const response = await request(requestObj, {
      params: requestParams,
    });
    return response;
  },
});

export const getItem = createEffect({
  name: 'getItem',
  async handler(gameId: IGame['id']) {
    const response = await request(api.games.getItem, {
      urlParams: { gameId },
    });
    return response;
  },
});

export const createGame = createEffect({
  name: 'createGame',
  async handler(data: UpdateCreateGameParams) {
    const { gameData, imagesData, tags } = data;
    const response = await request(api.games.add, {
      data: gameData,
    });

    if (tags) {
      await request(api.games.setTags, {
        urlParams: {
          gameId: response?.data?.body?.id,
        },
        data: { tags },
      });
    }

    try {
      await Promise.all(
        imagesData.map((image) => {
          image.append('id', response?.data?.body?.id);
          return request(api.games.image, {
            data: image,
          });
        }),
      );
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return await request(api.games.getItem, {
        urlParams: { gameId: response?.data?.body?.id },
      });
    }
  },
});

export const deleteGame = createEffect({
  name: 'deleteGame',
  async handler(gameId: IGame['id']) {
    const response = await request(api.games.delete, {
      urlParams: { gameId },
    });
    return response;
  },
});

export const updateGame = createEffect({
  name: 'updateGame',
  async handler(data: UpdateCreateGameParams) {
    const { id, gameData, imagesData, tags } = data;
    const response = await request(api.games.update, {
      data: gameData,
    });
    if (tags) {
      await request(api.games.setTags, {
        urlParams: {
          gameId: id,
        },
        data: { tags },
      });
    }

    try {
      await Promise.all(imagesData.map((image) => updateImage(image)));
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return await request(api.games.getItem, {
        urlParams: { gameId: response?.data?.body?.id },
      });
    }
  },
});

export const checkGameCode = createEffect({
  name: 'checkGameCode',
  async handler({ code }: CheckGameCodeParams) {
    const response = await request(api.games.checkCode, {
      urlParams: { code },
    });
    return response.data;
  },
});

export const updateImage = createEffect({
  name: 'updateImage',
  async handler(data: FormData) {
    return await request(api.games.image, {
      data,
    });
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'games',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

const preloaders = createPreloadersStore([getList, createGame, updateGame, checkGameCode]);

const { filters, setFilters } = createFiltersStore({
  listName: 'games',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { filters, setFilters };

const codeValidationResult = createStore<{
  valid: boolean;
  message?: string;
  code?: string;
  occupiedByGame?: string;
} | null>(null)
  .on(checkGameCode.doneData, (_, result) => {
    return {
      valid: !!result?.body.code && result?.status?.statusCode === 'OK',
      message: result.body.code,
      code: result.body.code,
      occupiedByGame: result?.body.occupiedByGame,
    };
  })
  .on(checkGameCode.failData, () => ({
    valid: false,
  }))
  .reset(reset);

export const resetCodeValidation = createEvent();

codeValidationResult.on(resetCodeValidation, () => null);

const list = createStore<IGame[]>([])
  .on(getList.done, (state, { result }) => get(result, ['data', 'body', 'content'], []))
  .on(createGame.done, (state, { result }) => {
    closeModal({ name: 'game' });

    if (result?.data?.body) {
      return [result.data.body, ...state];
    }
    return [...state];
  })
  .on(updateGame.done, (state, { result }) => {
    closeModal({ name: 'game' });
    const updatedItemIndex = state.findIndex((game) => result.data.body.id === game.id);
    const newState = [...state];
    if (updatedItemIndex !== -1) {
      newState.splice(updatedItemIndex, 1, result.data.body);
    }
    return newState;
  })
  .reset(reset);

const games = combine({
  preloaders,
  list,
  pagination,
  filters,
  defaultFilters,
  codeValidationResult,
});

export default games;
